import { createFileRoute } from "@tanstack/react-router"
import { useQuery } from "@tanstack/react-query"
import { Link } from "@tanstack/react-router"
import { Check, ChevronRight, Plus } from "lucide-react"
import { type Story } from "@yomimono/types"

import { Layout } from "@/components/layout"
import { CreateStoryDialog } from "@/components/create-story-dialog"
import { cn } from "@/lib/utils"
import { getStories } from "@/lib/api/stories"
import { storyLevelLabel } from "@/lib/story-level"
import { getStoryGeneration } from "@/lib/api/admin-stories"
import { processStoryHtml } from "@/lib/furigana-processor"
import { useIsAdmin } from "@/hooks/use-is-admin"
import { useIsDesktop } from "@/hooks/use-is-desktop"
import { Skeleton } from "@/components/ui/skeleton"

const EYEBROW =
  "font-mono text-xs uppercase tracking-[0.2em] text-muted-foreground"

function greeting(): string {
  const hour = new Date().getHours()
  if (hour < 12) return "Good morning"
  if (hour < 18) return "Good afternoon"
  return "Good evening"
}

function HeroSlogan({ centered = false }: { centered?: boolean }) {
  return (
    <div className={centered ? "text-center" : undefined}>
      {!centered && <p className={cn(EYEBROW, "mb-3")}>Your stories</p>}
      <h1
        className="text-[40px] leading-[1.05] font-semibold sm:text-[56px] sm:leading-tight"
        style={{ letterSpacing: "-2px" }}
      >
        {greeting()},<br />
        <span
          className="text-[oklch(0.62_0.09_35)]"
          style={{ fontWeight: 500 }}
        >
          let&apos;s read
        </span>{" "}
        something
      </h1>
    </div>
  )
}

// Admin-only per-story cost line. Fetches generation telemetry from the
// dedicated admin endpoint (shares the query cache with the detail page panel)
// rather than relying on the user-facing list response.
function StoryCardCost({ id }: { id: string }) {
  const { data: gen } = useQuery({
    queryKey: ["story", id, "generation"],
    queryFn: () => getStoryGeneration(id),
  })

  if (!gen || (gen.storyCostUsd === null && gen.quizCostUsd === null)) {
    return null
  }

  return (
    <p className="mt-1 text-xs text-muted-foreground">
      {gen.storyCostUsd !== null && `story: $${gen.storyCostUsd.toFixed(4)}`}
      {gen.storyCostUsd !== null && gen.quizCostUsd !== null && ", "}
      {gen.quizCostUsd !== null && `quiz: $${gen.quizCostUsd.toFixed(4)}`}
    </p>
  )
}

// A story counts as "read" once its quiz has been completed.
export function isRead(story: Story): boolean {
  return Boolean(story.quizCompletedAt)
}

// The library is one list, newest first.
export function sortStories(stories: Story[]): Story[] {
  return [...stories].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  )
}

// Little checkmark badge shown on a read story's row.
function ReadCheck({ className }: { className?: string }) {
  return (
    <span
      className={cn(
        "flex size-5 shrink-0 items-center justify-center rounded-full bg-muted-foreground/50 text-white",
        className
      )}
      title="Quiz completed"
      aria-label="Quiz completed"
    >
      <Check className="size-3" strokeWidth={3} />
    </span>
  )
}

const LIST_CLASS =
  "divide-y divide-[var(--surface-hairline)] overflow-hidden rounded-xl bg-[var(--surface-card)] desktop:rounded-2xl desktop:ring-1 desktop:ring-foreground/10"

const ROW_CLASS =
  "flex min-h-15 items-center gap-3 px-4 py-2.5 desktop:min-h-19 desktop:gap-4 desktop:px-6 desktop:py-3"

function StoryRow({ story, isAdmin }: { story: Story; isAdmin: boolean }) {
  const level = storyLevelLabel(story)
  return (
    <li>
      <Link
        to="/stories/$id"
        params={{ id: story.id }}
        className={cn(ROW_CLASS, "hover-flash")}
        aria-label={story.title}
      >
        <span className="flex min-w-0 flex-1 flex-col gap-0.5 desktop:gap-1">
          <span
            className="text-[17px] text-foreground desktop:text-[21px] [&_rt]:hidden"
            style={{ fontFamily: "var(--font-reading)" }}
            dangerouslySetInnerHTML={{
              __html: processStoryHtml(story.title, "none", false, []),
            }}
          />
          {level && (
            <span className="text-[13px] text-muted-foreground">{level}</span>
          )}
          {isAdmin && <StoryCardCost id={story.id} />}
        </span>
        {isRead(story) && <ReadCheck />}
        <ChevronRight
          aria-hidden="true"
          className="size-4 shrink-0 text-muted-foreground/60 desktop:hidden"
        />
      </Link>
    </li>
  )
}

function StoryListSkeleton() {
  return (
    <ul className={LIST_CLASS}>
      {[0, 1, 2].map((i) => (
        <li key={i} className={ROW_CLASS}>
          <div className="min-w-0 flex-1 space-y-2">
            <Skeleton className="h-5 w-2/3" />
            <Skeleton className="h-3 w-1/3" />
          </div>
        </li>
      ))}
    </ul>
  )
}

const WRITE_NEW_STORY = (
  <>
    <Plus data-icon="inline-start" className="size-4" />
    Write new story
  </>
)

// Round tinted "+" on the mobile title row (canvas `ios/Stories`).
const NEW_STORY_TRIGGER = (
  <button
    type="button"
    className="flex size-8 items-center justify-center rounded-full bg-primary/15 text-primary"
  >
    <Plus aria-hidden="true" className="size-4" strokeWidth={2.6} />
    <span className="sr-only">New story</span>
  </button>
)

function Stories() {
  const { data: stories = [], isPending } = useQuery({
    queryKey: ["stories"],
    queryFn: () => getStories(),
  })
  const isAdmin = useIsAdmin()
  const isDesktop = useIsDesktop()

  const isEmpty = !isPending && stories.length === 0
  const mobileAction = isDesktop ? undefined : (
    <CreateStoryDialog trigger={NEW_STORY_TRIGGER} />
  )

  if (isEmpty) {
    return (
      <Layout title="Stories" action={mobileAction}>
        <section className="pb-12">
          {isDesktop && <HeroSlogan centered />}
          <div className="flex flex-col items-center justify-center gap-6 py-24 text-center">
            <p className="text-muted-foreground">
              You have no stories yet. Write your first one!
            </p>
            <CreateStoryDialog triggerLabel={WRITE_NEW_STORY} />
          </div>
        </section>
      </Layout>
    )
  }

  return (
    <Layout title="Stories" action={mobileAction}>
      <section className="pb-12">
        {isDesktop && (
          <div className="mb-16 flex flex-row items-end justify-between gap-6">
            <HeroSlogan />
            <div className="shrink-0">
              <CreateStoryDialog triggerLabel={WRITE_NEW_STORY} />
            </div>
          </div>
        )}

        {isPending ? (
          <StoryListSkeleton />
        ) : (
          <ul className={LIST_CLASS}>
            {sortStories(stories).map((story) => (
              <StoryRow key={story.id} story={story} isAdmin={isAdmin} />
            ))}
          </ul>
        )}
      </section>
    </Layout>
  )
}

export const Route = createFileRoute("/_authenticated/stories/")({
  component: Stories,
})
